"use client";

import { useQueryState, parseAsString } from "nuqs";
import React, { useState, useEffect } from "react"; // Import useState and useEffect hooks
import { Image, Link } from "@heroui/react";
import { Suspense } from "react";
import {
  Chip,
  Tabs,
  Tab,
  Button,
  Accordion,
  AccordionItem,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/react";
import DecklistsTable from "@/components/Decklists/DecklistsTable";
import ColorFilter, { ColorFilterUA } from "@/components/Filters/ColorFilter";
import EventTypesFilter, {
  EventTypesFilterUA,
} from "@/components/Filters/EventTypesFilter";
import LeaderFilter4 from "@/components/DeckBuilder/LeaderFilter4";
import { getLeaderId } from "@/lib/leaderManager";
import { useLeaders } from "@/hooks/useLeaders";
import {
  IconAdjustmentsHorizontal,
  IconCards,
  IconChartBar,
  IconLetterESmall,
  IconLetterWSmall,
  IconLetterGSmall,
  IconChevronDown,
} from "@tabler/icons-react";
import { myDayjs } from "@/lib/dayjsHelper";
import { IDecklistV2, RegionDecklists, DecklistParam } from "@/models/decklist";
import DeckDistribution from "@/components/Decklists/DeckDistribution";
import LeaderPieChartWithFilters from "@/components/MetaReport/LeaderPieChart";
import DomainVideoAd from "@/components/Ads/DomainVideoAd";
import "./decklists.css";
import { FormatReleaseDate, useSeriesStore } from "@/stores/seriesStore";
import { GameConfig, SupportedGames } from "@/types/game";
import { getGameHandler } from "@/games";
import { formatFormat } from "@/lib/formatHelper";
import UASetFilter from "@/components/Filters/UASetFilter";
import { getGame, getGameByTcgplayerCategoryId } from "@/games";
import { getLeaderData as getIdentityData } from "@/lib/archetypeManager";
import type { PalworldItem } from "@/interfaces/CardItem";
import type { PalworldAaData } from "@/lib/games/palworld/getCardData";
import { setPalworldCards } from "@/lib/games/palworld/getCardDataLocal";

export default function DecklistsSuspense({
  decklistParams,
  regionDecklists,
  game,
  gameConfig,
  palworldCards,
  palworldAaData,
  palworldArchetypes,
}: {
  decklistParams: DecklistParam[];
  regionDecklists: RegionDecklists[];
  game: SupportedGames;
  gameConfig: GameConfig;
  palworldCards?: PalworldItem[];
  palworldAaData?: PalworldAaData;
  palworldArchetypes?: Record<string, any>;
}) {
  return (
    <Suspense>
      <Decklists
        decklistParams={decklistParams}
        regionDecklists={regionDecklists}
        game={game}
        gameConfig={gameConfig}
        palworldCards={palworldCards}
        palworldAaData={palworldAaData}
        palworldArchetypes={palworldArchetypes}
      />
    </Suspense>
  );
}

function getSet(selectedSet: string) {
  return selectedSet.split("-")[1].toUpperCase();
}

function Decklists({
  decklistParams,
  regionDecklists,
  game,
  gameConfig,
  palworldCards,
  palworldAaData,
  palworldArchetypes,
}: {
  decklistParams: DecklistParam[];
  regionDecklists: RegionDecklists[];
  game: SupportedGames;
  gameConfig: GameConfig;
  palworldCards?: PalworldItem[];
  palworldAaData?: PalworldAaData;
  palworldArchetypes?: Record<string, any>;
}) {
  // Declare state variables to store decklists data and colors
  // console.log(decks);

  const [decklists, setDecklists] = useState<IDecklistV2[]>([]); // Provide correct type for decklists state variable
  const [setFilter, setSetFilter] = useState("");
  const [colorFilter, setColorFilter] = useState<Set<string>>(new Set());
  const [eventTypeFilter, setEventTypeFilter] = useState<Set<string>>(
    new Set(),
  );
  const [leaderFilter, setLeaderFilter] = useState("");
  const [nameFilter, setNameFilter] = useQueryState(
    "name",
    parseAsString.withDefault(""),
  );
  const [error, setError] = useState<Error | unknown>(null); // Specify the type of error as 'Error' or 'null'
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSet, setSelectedSet] = useQueryState(
    "set",
    parseAsString.withDefault(regionDecklists[0]?.id || ""),
  );

  const formatReleaseDates = useSeriesStore(
    (state) => state.formatReleaseDates,
  );
  const fetchFormatReleaseDates = useSeriesStore(
    (state) => state.fetchFormatReleaseDates,
  );
  const gameHandler = getGameHandler(game);

  useEffect(() => {
    // Fetch format release dates if not already loaded
    fetchFormatReleaseDates();
  }, [fetchFormatReleaseDates]);

  // Set Palworld cards on client side for archetype derivation
  useEffect(() => {
    if (game === "palworld" && palworldCards && palworldAaData) {
      setPalworldCards(palworldCards, palworldAaData);
      console.log("[Decklists] Set Palworld cards:", palworldCards.length);
    }
  }, [game, palworldCards, palworldAaData]);

  useEffect(() => {
    if (selectedSet === "") {
      setDecklists(regionDecklists[0]?.decklists || []);
    } else {
      for (const curFormat of regionDecklists) {
        if (curFormat.id === selectedSet) {
          setDecklists(curFormat.decklists);
        }
      }
    }
    setIsLoading(false);
  }, [selectedSet, regionDecklists]);

  let hasFilter = false;
  if (
    colorFilter.size != 0 ||
    eventTypeFilter.size != 0 ||
    nameFilter != "" ||
    leaderFilter != "" ||
    setFilter != ""
  ) {
    hasFilter = true;
  }

  const onClear = React.useCallback(() => {
    setColorFilter(new Set());
    setEventTypeFilter(new Set());
    setNameFilter("");
  }, [setNameFilter]);

  // Handle empty regionDecklists - must be after all hooks
  if (regionDecklists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        <h2 className="text-2xl font-bold mb-2">No Decklists Available</h2>
        <p className="text-slate-600 dark:text-slate-400 mb-4">
          Decklist data is currently unavailable. Please check back later.
        </p>
        {game === "naruto" && (
          <div className="mt-4 p-4 bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg max-w-md">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Debug Info:</strong> Naruto decklists require backend API
              access. Check{" "}
              <code className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
                /api/debug-naruto
              </code>{" "}
              for details.
            </p>
          </div>
        )}
      </div>
    );
  }

  // useEffect(() => {
  //   async function fetchData() {
  //     try {
  //       const response = await fetch("/api/decklists"); // Replace '/api/data' with your API endpoint
  //       if (!response.ok) {
  //         throw new Error("Failed to fetch data");
  //       }
  //       const result = await response.json();
  //       setDecklists(result);
  //       setIsLoading(false);
  //     } catch (error) {
  //       setError(error);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   }

  //   fetchData();
  // }, []);

  let regionColor = "primary";
  if (selectedSet.toLowerCase().includes("east")) {
    regionColor = "danger";
  } else if (selectedSet.toLowerCase().includes("global")) {
    regionColor = "primary"; // Changed from "success" (green) to "primary" (blue)
  }

  // Get the currently selected region's title
  const selectedRegion = regionDecklists.find((d) => d.id === selectedSet);
  const selectedTitle = selectedRegion?.title || "Select Format";

  // Use Tabs for One Piece, Dropdown for Naruto
  const regionFilter =
    game === "naruto" ? (
      <Dropdown>
        <DropdownTrigger>
          <Button
            color="default"
            variant="flat"
            className="capitalize pl-4"
            endContent={<IconChevronDown size={18} className="-ml-1" />}
          >
            {selectedTitle}
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Format selection"
          selectionMode="single"
          selectedKeys={selectedSet ? [selectedSet] : []}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0];
            if (selected) {
              setSelectedSet(selected as string);
            }
          }}
        >
          {regionDecklists.map((d) => (
            <DropdownItem key={d.id}>{d.title}</DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    ) : (
      <Tabs
        selectedKey={selectedSet}
        onSelectionChange={(key) => setSelectedSet(key as string)}
        color={regionColor as any}
        size="lg"
      >
        {regionDecklists.map((d) => (
          <Tab key={d.id} title={d.title} />
        ))}
      </Tabs>
    );

  const filteredDecklists: IDecklistV2[] = filterDecklists(
    game,
    decklists,
    setFilter,
    colorFilter,
    eventTypeFilter,
    nameFilter,
    leaderFilter,
  );

  // Render component
  return (
    <div>
      <div className="overflow-x-scroll no-visible-scroll">
        {gameConfig.hasFormatReleaseDates && (
          <FormatReleaseDates formatReleaseDates={formatReleaseDates} />
        )}
      </div>
      <div className="flex flex-row items-center gap-4">
        {selectedSet && (
          <div className="md:block min-h-[100px] min-w-[100px]">
            <Image
              alt={`${formatFormat(getSet(selectedSet))}`}
              height={100}
              width={100}
              src={gameHandler.getSetImage(getSet(selectedSet))}
            />
          </div>
        )}
        <div>
          <h1 className="inline-block text-2xl mb-2 sm:text-4xl font-semibold text-slate-900 tracking-tight dark:text-slate-100">
            Best {gameConfig.gameName} Decklists{" "}
            {selectedSet && formatFormat(getSet(selectedSet))}
          </h1>
          <h2 className="hidden md:block mt-2 text-slate-700 dark:text-slate-300 text-lg">
            {game === "palworld" ? (
              <>
                {gameConfig.gameAbbreviation}{" "}
                {getLatestFormat(decklistParams, "global")} winning decklists
                from masters league, challengers cup, and shop tournaments.
              </>
            ) : game === "riftbound" ||
              game === "naruto" ||
              game === "one-piece" ? (
              <>
                {gameConfig.gameAbbreviation}{" "}
                {getLatestFormat(
                  decklistParams,
                  game === "naruto" || game === "one-piece" ? "global" : "east",
                )}{" "}
                winning decklists from championship events, regionals, and
                flagship tournaments. Updated daily.
              </>
            ) : (
              <>
                {gameConfig.gameAbbreviation} Eastern (
                {getLatestFormat(decklistParams, "east")}) and Western (
                {getLatestFormat(decklistParams, "west")}) winning decklists
                from championship events, regionals, and flagship tournaments.
                Updated daily.
              </>
            )}
          </h2>
        </div>
      </div>
      <h2 className="md:hidden mt-2 text-slate-700 dark:text-slate-300 text-lg">
        {game === "palworld" ? (
          <>
            {gameConfig.gameAbbreviation}{" "}
            {getLatestFormat(decklistParams, "global")} winning decklists from
            masters league, challengers cup, and shop tournaments.
          </>
        ) : game === "riftbound" ||
          game === "naruto" ||
          game === "one-piece" ? (
          <>
            {gameConfig.gameAbbreviation}{" "}
            {getLatestFormat(
              decklistParams,
              game === "naruto" || game === "one-piece" ? "global" : "east",
            )}{" "}
            winning decklists from championship events. Updated daily.
          </>
        ) : (
          <>
            {gameConfig.gameAbbreviation} Eastern (
            {getLatestFormat(decklistParams, "east")}) and Western (
            {getLatestFormat(decklistParams, "west")}) winning decklists from
            championship events. Updated daily.
          </>
        )}
      </h2>

      <div className="my-4 overflow-scroll no-visible-scroll">
        {regionFilter}
      </div>
      {/* <div className="bg-blue-500/40 p-4 rounded-lg border border-blue-500">
        <div className="opacity-100">
          <IconInfoCircle
            className="inline-block relative bottom-[2px]"
            height={20}
            width={20}
          />{" "}
          Notice: OP08 is not eligible for flagship events during the first week
          of release. Flagship events from May 25th to May 31st will use the
          OP07 filter.
        </div>
      </div> */}
      {gameConfig.hasLeaderDistribution && (
        <div className="-mx-[1px]">
          <Accordion
            isCompact
            variant="light"
            className="my-4 px-0"
            itemClasses={{
              trigger: "bg-default-50 px-4 rounded-md",
            }}
          >
            <AccordionItem
              key="1"
              aria-label="Leader Distribution"
              title={
                <div className="flex flex-row gap-2 items-center">
                  <IconChartBar />
                  Leader Distribution
                </div>
              }
            >
              <DeckDistribution decks={decklists} game={game} />
            </AccordionItem>
          </Accordion>
        </div>
      )}

      {gameConfig.hasPieChart && (
        <div className="mb-4 flex flex-row rounded-md">
          <div className="h-[360px] flex items-center justify-center bg-slate-900 pie-chart-wrapper w-full">
            <LeaderPieChartWithFilters
              decklists={decklists}
              game={game}
              palworldArchetypes={palworldArchetypes}
            />
          </div>
          <div className="relative h-[360px] min-w-[640px] video-desktop">
            <DomainVideoAd screen={"desktop"} alias="decklists-video-desktop" />
          </div>
        </div>
      )}

      <div className="video-mobile mb-4 h-[250px]">
        <DomainVideoAd screen={"mobile"} alias="decklists-video-mobile" />
      </div>

      {/* flex w-full flex-wrap items-end md:flex-nowrap mb-6 md:mb-0 gap-4 */}
      <div className="flex flex-wrap w-full flex-row gap-2 my-2 items-center">
        <div className="hidden lg:visible">
          <IconAdjustmentsHorizontal />
        </div>
        {game === "one-piece" && (
          <>
            <LeaderFilter4
              selected={leaderFilter}
              setSelected={(leaderId) => setLeaderFilter(leaderId || "")}
            />
            <ColorFilter
              className="w-[100px]"
              selected={colorFilter}
              setSelected={setColorFilter}
            />
            <EventTypesFilter
              selected={eventTypeFilter}
              setSelected={setEventTypeFilter}
            />
          </>
        )}
        {game === "union-arena" && (
          <>
            <UASetFilter
              selected={setFilter}
              setSelected={(setId) => setSetFilter(setId || "")}
            />
            <ColorFilterUA
              className="w-[100px]"
              selected={colorFilter}
              setSelected={setColorFilter}
            />
            <EventTypesFilterUA
              selected={eventTypeFilter}
              setSelected={setEventTypeFilter}
            />
          </>
        )}

        {hasFilter && (
          <Button
            color="primary"
            variant="light"
            onPress={onClear}
            className={"hidden lg:visible"}
          >
            Reset
          </Button>
        )}
      </div>

      <div className="mt-4 mb-4 flex flex-row gap-2 items-center">
        <Chip startContent={<IconCards size={18} />} variant="flat">
          {filteredDecklists.length} Decks
        </Chip>
        {getSubmitForm(game) && (
          <Button
            color="primary"
            as={Link}
            href={getSubmitForm(game)}
            target="_blank"
          >
            Submit Decklist
          </Button>
        )}
      </div>

      <DecklistsTable
        game={game}
        isLoading={isLoading}
        decklists={filteredDecklists}
        palworldArchetypes={palworldArchetypes}
      />
      {/* <pre>{JSON.stringify(decklists, null, 2)}</pre> */}
    </div>
  );
}

function getLatestFormat(
  decklistParams: DecklistParam[],
  region: "east" | "west" | "global",
): string {
  for (const d of decklistParams) {
    if (d.region === region) {
      return formatFormat(d.format);
    }
  }

  return "";
}

function FormatReleaseDates({
  formatReleaseDates,
}: {
  formatReleaseDates: Record<string, FormatReleaseDate[]>;
}) {
  let releasesData = [];

  let daysBefore = 14;
  let daysAfter = 30;

  if (Object.keys(formatReleaseDates).length > 0) {
    if ("GLOBAL" in formatReleaseDates) {
      for (const globalRelease of formatReleaseDates["GLOBAL"].toReversed()) {
        const diffDays = myDayjs().diff(
          myDayjs(globalRelease["release_date"]),
          "day",
        );
        let showRelease = diffDays <= daysBefore && diffDays >= -daysAfter;

        if (showRelease) {
          releasesData.push({
            region: "global",
            set: globalRelease["set"],
            releaseDate: globalRelease["release_date"].replace(/\/\d{4}$/, ""),
            fullReleaseDate: globalRelease["release_date"],
          });
        }
      }
    }
    if ("ASIA-EN" in formatReleaseDates) {
      for (const asiaRelease of formatReleaseDates["ASIA-EN"].toReversed()) {
        const diffDays = myDayjs().diff(
          myDayjs(asiaRelease["release_date"]),
          "day",
        );
        let showRelease = diffDays <= daysBefore && diffDays >= -daysAfter;

        if (showRelease) {
          releasesData.push({
            region: "east",
            set: asiaRelease["set"],
            releaseDate: asiaRelease["release_date"].replace(/\/\d{4}$/, ""),
            fullReleaseDate: asiaRelease["release_date"],
          });
        }
      }
    }
    if ("EN" in formatReleaseDates) {
      for (const enRelease of formatReleaseDates["EN"].toReversed()) {
        const diffDays = myDayjs().diff(
          myDayjs(enRelease["release_date"]),
          "day",
        );
        let showRelease = diffDays <= daysBefore && diffDays >= -daysAfter;
        if (showRelease) {
          releasesData.push({
            region: "west",
            set: enRelease["set"],
            releaseDate: enRelease["release_date"].replace(/\/\d{4}$/, ""),
            fullReleaseDate: enRelease["release_date"],
          });
        }
      }
    }
  }

  releasesData.sort(
    (a, b) =>
      new Date(a.fullReleaseDate).getTime() -
      new Date(b.fullReleaseDate).getTime(),
  );

  let releases = [];
  for (const r of releasesData) {
    releases.push(
      <Chip key={`${r["region"]}-${r["releaseDate"]}-${r["set"]}`}>
        <div className="flex flex-row items-center">
          {r["region"] === "east" ? (
            <IconLetterESmall className="text-danger-500 -ml-[8px]" />
          ) : r["region"] === "global" ? (
            <IconLetterGSmall className="text-primary-500 -ml-[8px]" />
          ) : (
            <IconLetterWSmall className="text-primary-500 -ml-[8px]" />
          )}
          {r["set"]} {r["releaseDate"]}
        </div>
      </Chip>,
    );
  }

  return (
    <div className="flex flex-row gap-2 mb-4 min-h-[28px]">{releases}</div>
  );
}

/**
 * One filter for every game.
 *
 * This was two ~70-line functions that differed only in which identity filter
 * they applied - One Piece matched the leader id, Union Arena matched the
 * archetype's set. Both filters are skipped when their value is empty, so
 * applying both is identical for each game and correct for a new one.
 */
function filterDecklists(
  game: SupportedGames,
  decklists: IDecklistV2[],
  setFilter: string,
  colorFilter: Set<string>,
  eventTypeFilter: Set<string>,
  nameFilter: string,
  leaderFilter: string,
) {
  const filteredDecklists: IDecklistV2[] = [];

  for (let i = 0; i < decklists.length; i++) {
    const identityId = decklists[i].leader_id;

    // Resolve the decklist's own game from its TCGplayer category id rather
    // than hardcoding 89 for Riftbound. Anything else previously fell through
    // to One Piece's leader data, which is wrong for Union Arena.
    const decklistGame =
      getGameByTcgplayerCategoryId(decklists[i].game) ?? game;
    const identity = getIdentityData(decklistGame, identityId);

    // Allow Palworld decklists without archetype data
    if (!identity && game !== "palworld") {
      continue;
    }

    let setMatch = true;
    if (setFilter && setFilter !== "" && identity) {
      setMatch = identity.set === setFilter;
    }

    let leaderMatch = true;
    if (leaderFilter && leaderFilter !== "") {
      leaderMatch = identityId === leaderFilter;
    }

    let nameMatch = true;
    if (nameFilter !== "" && identity) {
      nameMatch = identity.name
        .toLowerCase()
        .includes(nameFilter.toLowerCase());
    }

    let colorMatch = true;
    if (colorFilter.size !== 0 && identity) {
      const colorsToSearch = Array.from(colorFilter);
      const curColors = identity.color
        .split("/")
        .map((element: string) => element.toLowerCase());
      colorMatch = curColors.some((color: string) =>
        colorsToSearch.includes(color),
      );
    }

    let eventTypeMatch = true;
    if (eventTypeFilter.size !== 0) {
      const eventTypesToSearch = Array.from(eventTypeFilter);
      const tournamentType = decklists[i].tournament_type;

      // Check if the tournament type matches any selected filter
      eventTypeMatch =
        eventTypesToSearch.includes(tournamentType) ||
        // Handle Regional cases
        (tournamentType === "Regional" &&
          (eventTypesToSearch.includes("Regional") ||
            eventTypesToSearch.includes("Regional Offline") ||
            eventTypesToSearch.includes("Regional Online"))) ||
        // Handle specific regional types
        ((tournamentType === "Regional Offline" ||
          tournamentType === "Regional Online") &&
          eventTypesToSearch.includes("Regional"));
    }

    const all: Boolean[] = [
      nameMatch,
      setMatch,
      leaderMatch,
      colorMatch,
      eventTypeMatch,
    ];
    if (all.every((v) => v)) {
      filteredDecklists.push(decklists[i]);
    }
  }

  return filteredDecklists;
}

function getSubmitForm(game: SupportedGames): string | undefined {
  return (getGame(game) as { submitFormUrl?: string }).submitFormUrl;
}
